import {useState} from 'react';
import {Platform} from 'react-native';

import type {CartStore} from '../../../stores/cartStore';
import {Analytics} from '../../../utils/analytics/analytics';
import {EventName, EventScreenName, EventSourceType} from '../../../utils/analytics/events';
import {getWeb3DsBrowserData} from '../util/threeDS';

/**
 * Browser data required for 3D Secure authentication.
 * See: https://gateway.tillpayments.com/documentation/apiv3#transaction-data-3d-secure-2-0-device-data
 */
export interface BrowserData {
  java_enabled: boolean;
  language: string;
  color_depth: number;
  screen_height: number;
  screen_width: number;
  timezone: number;
}

export interface UseOrderSubmissionParams {
  cartStore: CartStore;
  /** For mobile: browserData from ThreeDsMobileBrowserDataIframe via useFetchMobileWebView3dsBrowserDataJSFocusEffect */
  browserData?: BrowserData;
  /** Payment card ID for card payments */
  paymentCardId?: string;
  /** Validation function - should return true if order is valid */
  validateOrder: () => boolean;
}

export interface UseOrderSubmissionResult {
  /** Show confirmation modal and validate order first */
  handlePlaceOrder: () => void;
  /** Submit order to API (call after user confirms in modal) */
  submitOrder: () => void;
  /** Confirmation modal visibility state */
  isConfirmModalVisible: boolean;
  /** Close confirmation modal */
  closeConfirmModal: () => void;
}

/**
 * Consolidates order submission logic for checkout payment screens.
 *
 * Handles:
 * - Validation before showing confirmation modal
 * - Browser data retrieval (web: direct, mobile: passed from hook)
 * - Order submission via cartStore.checkoutOrder()
 * - Analytics logging
 *
 * Used by:
 * - CheckoutPaymentScreen (mobile)
 * - UnifiedCheckoutScreen (web desktop)
 */
export function useOrderSubmission({
  cartStore,
  browserData: mobileBrowserData,
  paymentCardId,
  validateOrder,
}: UseOrderSubmissionParams): UseOrderSubmissionResult {
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);

  /**
   * Get browser data for 3DS authentication.
   * - Web: Collect directly from browser window
   * - Mobile: Use pre-collected data from WebView iframe
   */
  const getBrowserData = (): BrowserData => {
    if (Platform.OS === 'web') {
      return getWeb3DsBrowserData();
    }
    // Mobile: use browserData passed from useFetchMobileWebView3dsBrowserDataJSFocusEffect
    return mobileBrowserData || ({} as BrowserData);
  };

  /**
   * Validate order and show confirmation modal if valid.
   * Call this when user presses "Place Order" / "Pay Now" button.
   */
  const handlePlaceOrder = () => {
    if (validateOrder()) {
      setIsConfirmModalVisible(true);
    }
  };

  /**
   * Submit order to API after user confirms.
   * - Closes modal immediately to prevent blocking navigation (e.g., to 3DS screen)
   * - Calls cartStore.checkoutOrder with browser data and payment card
   * - Logs analytics event
   */
  const submitOrder = () => {
    // Close modal immediately to prevent blocking navigation (e.g., to 3DS screen)
    setIsConfirmModalVisible(false);

    const browserData = getBrowserData();

    // Submit order - cartStore handles navigation to processing screen
    cartStore.checkoutOrder(
      // Navigation is handled internally by cartStore via RootNavigation
      undefined,
      browserData,
      paymentCardId,
    );

    // Log analytics
    Analytics.logEvent({
      eventName: EventName.SubmitOrder,
      parameters: {
        screen_name: EventScreenName.Checkout,
        source_type: EventSourceType.Button,
        value: (cartStore.currentOrder?.total?.cents_inc_tax ?? 0) / 100,
        tax: (cartStore.currentOrder?.total?.cents_tax ?? 0) / 100,
        currency: 'AUD',
      },
    });
  };

  const closeConfirmModal = () => {
    setIsConfirmModalVisible(false);
  };

  return {
    handlePlaceOrder,
    submitOrder,
    isConfirmModalVisible,
    closeConfirmModal,
  };
}

export default useOrderSubmission;
